import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { ExternalLink, Link, ThumbsUp } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Search } from "@/models/search/Search";
import { buildSearchUrlParams } from "@/utils/searchParamsFormatter";
import {
  buildSourceSearchUrl,
  isSourceSearch,
  toSourceSearchTableState,
} from "@/lib/search/sourceSearch";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { logger } from "@/lib/logger";
import useSettingsStore from "@/stores/settings.store";

interface CopyLinkButtonProps {
  searchQuery?: Search;
  externalLink?: string;
  buttonClassName?: string;
  linkClassName?: string;
  svgSize?: number;
  toolTipLocation?: "top" | "bottom";
}

const CopyLinkButton = ({
  searchQuery,
  externalLink,
  buttonClassName,
  linkClassName,
  svgSize,
  toolTipLocation = "bottom",
}: CopyLinkButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const [showSvg, setShowSvg] = useState(false);
  const { isDarkMode } = useSettingsStore();
  useEffect(() => {
    if (isCopied) {
      const svgDelay = 500;
      const resetDelay = 1800;

      const svgTimer = setTimeout(() => setShowSvg(true), svgDelay);
      const resetTimer = setTimeout(() => {
        setIsCopied(false);
        setShowSvg(false);
      }, resetDelay);

      return () => {
        clearTimeout(svgTimer);
        clearTimeout(resetTimer);
      };
    }
  }, [isCopied]);

  const getLegislationUrl = () => {
    try {
      const url = new URL(window.location.origin);
      const hostname = url.hostname;

      // Check if hostname contains "loda" or "app" and add "legislation." before it
      if (hostname.includes("loda")) {
        url.hostname = hostname.replace("loda", "lovgivning.loda");
      } else if (hostname.includes("app")) {
        url.hostname = hostname.replace("app", "lovgivning");
      } else if (hostname.includes("localhost")) {
        url.hostname = hostname.replace("localhost", "dev.lovgivning.lara.law");
        url.port = "";
      }

      return url.toString();
    } catch (error) {
      logger.warn("Failed to build the legislation site URL", {
        operation: "copyLegislationLink",
        error,
      });
      return window.location.origin;
    }
  };

  const handleCopy = () => {
    if (externalLink) {
      const legislationUrl = getLegislationUrl();
      navigator.clipboard.writeText(legislationUrl + externalLink);
      setIsCopied(true);
      return;
    }
    if (searchQuery) {
      // A source search reruns on the Kilder page, not in the search modal.
      const path = isSourceSearch(searchQuery)
        ? buildSourceSearchUrl(toSourceSearchTableState(searchQuery))
        : `/search?${buildSearchUrlParams(searchQuery).toString()}`;
      navigator.clipboard.writeText(`${window.location.origin}${path}`);
      setIsCopied(true);
      return;
    }
  };

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={
              buttonClassName ??
              "absolute right-2 top-1/2 -translate-y-1/2 size-6 group"
            }
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
          >
            <AnimatePresence mode="wait">
              {isCopied ? (
                <motion.div
                  key="thumbs-up"
                  className="relative flex items-center justify-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeIn" }}
                >
                  {showSvg && (
                    <motion.div
                      className={cn(
                        "absolute",
                        externalLink ? "-top-2" : "-top-3"
                      )}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                        mass: 0.8,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="15"
                        height="8"
                        viewBox="0 0 15 8"
                        fill="none"
                      >
                        <path
                          d="M7.73438 1.20449L7.73511 4.20388M4.2703 6.20429L1.67215 4.70371M13.7972 4.70392L11.1983 6.2044"
                          stroke={isDarkMode ? "white" : "black"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ rotate: 25, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    className="flex items-center justify-center"
                  >
                    <ThumbsUp
                      className={`text-black fill-warning ${
                        svgSize ? `size-${svgSize}` : ""
                      }`}
                    />
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="external-link"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center justify-center"
                >
                  {externalLink ? (
                    <ExternalLink
                      className={`${linkClassName} group-hover:text-primary/70 ${
                        svgSize ? `size-${svgSize}` : ""
                      }`}
                    />
                  ) : (
                    <Link
                      className={`${linkClassName} group-hover:text-primary ${
                        svgSize ? `size-${svgSize}` : ""
                      }`}
                    />
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </TooltipTrigger>
        <TooltipContent
          side={toolTipLocation}
          align="center"
          className={`z-300 ${externalLink ? "max-w-[200px]" : ""}`}
        >
          {externalLink
            ? "Del link eksternt - hvis du vil dele dokumentet med en som har adgang til platformen, så kopier url'en i stedet"
            : "Del søgning"}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default CopyLinkButton;
